import { useState } from 'react';
import { useMeContext } from '../app/MeContext';
import { useSheet, useToast } from '../app/shell-context';
import { ApiError } from '../api/client';
import { formatTime } from '../lib/time';
import { formatKop } from '../lib/money';
import { Avatar, Notice } from '../ui/parts';
import { useAppointmentAction, useAppointmentCard, useColleagues, useCreateReferral, useSaveNote, useStaffSlots } from './api';
import type { AgendaItem } from './types';

/**
 * Карточка приёма со всеми действиями. Команда ребёнка видна целиком, а заметка
 * — только своя: коллеги её не читают, и об этом в интерфейсе написано прямо,
 * иначе специалист не поймёт, почему не видит запись коллеги.
 */
export function AppointmentSheet({ item }: { item: AgendaItem }) {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const card = useAppointmentCard(item.id);
  const action = useAppointmentAction();
  const tz = me.center.tz;
  const tf = me.center.timeFormat;

  const done = item.status === 'completed' || item.status === 'no_show';
  /**
   * Занятие закрывается само по окончании, поэтому `completed` — это ещё не финал:
   * специалист узнаёт о неявке позже, чем сработало автозавершение, и должен успеть
   * поправить. Неявка — уже окончательное решение, её переигрывать нечем.
   */
  const fixable = item.status === 'completed';
  const paid = item.payment_status === 'paid';

  function run(v: Parameters<typeof action.mutate>[0], ok: string) {
    action.mutate(v, {
      onSuccess: () => {
        toast(ok);
        sheet.close();
      },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
    });
  }

  return (
    <>
      <h3>{item.child_name}</h3>
      <p>
        {item.service_title} · в {formatTime(item.starts_at_utc, tz, tf)}
        {item.status === 'no_show' ? ' · клиент не пришёл' : ''}
        {item.status === 'completed' ? ' · запись состоялась' : ''}
      </p>

      <div className="kv">
        <div>
          <span className="k">Оплата</span>
          <span className="v">{paid ? 'получена' : 'не получена'} · {formatKop(item.price_kop)}</span>
        </div>
        <div>
          <span className="k">Родитель</span>
          <span className="v">{item.parent_name} · {item.phone}</span>
        </div>
        {intakeNote(item) ? (
          <div>
            <span className="k">Запрос родителя</span>
            <span className="v" style={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{intakeNote(item)}</span>
          </div>
        ) : null}
      </div>

      {card.data?.team && card.data.team.length > 1 ? (
        <>
          <p className="eyebrow">Ребёнка также ведут</p>
          <div className="rows">
            {card.data.team
              .filter((t) => t.specialist_id !== me.specialistId)
              .map((t) => (
                <div className="rw" key={t.specialist_id}>
                  <Avatar name={t.specialist_name} photo={t.specialist_photo} small />
                  <span className="rw-body">
                    <b>{t.specialist_name}</b>
                    <span>{t.direction_title}</span>
                  </span>
                </div>
              ))}
          </div>
        </>
      ) : null}

      {done ? null : (
        <>
          <div className="row-actions">
            <button className="btn" onClick={() => sheet.open(<MoveSheet item={item} />)}>Перенести</button>
            <button className="btn" onClick={() => sheet.open(<NoteSheet item={item} initial={card.data?.note ?? item.my_note ?? ''} />)}>
              {item.my_note ? 'Изменить заметку' : 'Моя заметка'}
            </button>
          </div>
          <div className="row-actions">
            <button className="btn" disabled={action.isPending || paid} onClick={() => run({ id: item.id, action: 'mark-paid' }, 'Оплата отмечена')}>
              {paid ? 'Оплачено' : 'Отметить оплату'}
            </button>
          </div>
          <div className="row-actions">
            <button className="btn solid" onClick={() => sheet.open(<ReferSheet item={item} />)}>Направить к коллеге</button>
          </div>
          <div className="row-actions">
            <button className="btn ghost-danger" onClick={() => sheet.open(<NoShowSheet item={item} />)}>Клиент не пришёл</button>
            <button className="btn solid-danger" onClick={() => sheet.open(<CancelSheet item={item} />)}>Отменить запись</button>
          </div>
        </>
      )}

      {done ? (
        <>
          {fixable ? (
            <div className="row-actions">
              <button className="btn" disabled={action.isPending || paid} onClick={() => run({ id: item.id, action: 'mark-paid' }, 'Оплата отмечена')}>
                {paid ? 'Оплачено' : 'Отметить оплату'}
              </button>
              <button className="btn ghost-danger" onClick={() => sheet.open(<NoShowSheet item={item} />)}>Клиент не пришёл</button>
            </div>
          ) : null}
          <button className="btn" onClick={() => sheet.open(<NoteSheet item={item} initial={card.data?.note ?? item.my_note ?? ''} />)}>
            {item.my_note ? 'Изменить заметку' : 'Моя заметка'}
          </button>
        </>
      ) : null}

      <button className="btn" onClick={() => sheet.close()}>Закрыть</button>
    </>
  );
}

/**
 * Направление к коллеге. Специалист не закрепляет ребёнка сам — он просит, а
 * закрепляет администратор: иначе двое одновременно «возьмут» одного ребёнка
 * в одном направлении. Заявка уходит администратору всегда, коллеге —
 * дополнительно, если его выбрали.
 */
function ReferSheet({ item }: { item: AgendaItem }) {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const colleagues = useColleagues();
  const refer = useCreateReferral();
  const [specialistId, setSpecialistId] = useState<number | null>(null);
  const [note, setNote] = useState('');

  // Себя не предлагаем и архивных тоже: направляют к тому, кто ведёт приём.
  const list = (colleagues.data ?? []).filter((c) => !c.isMe && c.status !== 'archived');
  const chosen = list.find((c) => c.id === specialistId) ?? null;

  return (
    <>
      <h3>Направить к коллеге</h3>
      <p>
        {item.child_name}. Закрепляет администратор — вы описываете, что видите, и, если знаете,
        к кому именно.
      </p>

      <div className="field">
        <label htmlFor="refWho">Кому</label>
        <select id="refWho" value={specialistId ?? ''} onChange={(e) => setSpecialistId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">Не знаю — пусть подберёт администратор</option>
          {list.map((c) => (
            <option key={c.id} value={c.id}>
              {c.displayName} · {c.directionTitle}{c.status === 'paused' ? ' (пауза)' : ''}
            </option>
          ))}
        </select>
        {colleagues.isPending ? <span className="hint">Загружаем состав организации…</span> : null}
      </div>

      <div className="field">
        <label htmlFor="refNote">Что передать</label>
        <textarea
          id="refNote"
          placeholder="Речь отстаёт от возраста, стоит показать логопеду"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <span className="hint">Это увидят администратор и выбранный коллега — родитель нет.</span>
      </div>

      {chosen?.status === 'paused' ? (
        <Notice>У {chosen.displayName} сейчас пауза записи — заявка дойдёт, но ответ может задержаться.</Notice>
      ) : null}

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={refer.isPending || note.trim().length < 3}
          onClick={() =>
            refer.mutate(
              {
                childId: item.child_id,
                directionId: chosen ? chosen.directionId : null,
                preferredSpecialistId: chosen ? chosen.id : null,
                note: note.trim(),
              },
              {
                onSuccess: () => {
                  toast(chosen ? `Заявка ушла администратору и коллеге — ${chosen.displayName}` : 'Заявка ушла администратору');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось отправить заявку'),
              },
            )
          }
        >
          Отправить заявку
        </button>
      </div>
      <p className="footnote" style={{ textAlign: 'left' }}>
        Контакт организации для вопросов: {me.center.adminContact || me.center.phone}
      </p>
    </>
  );
}

/** Заметка личная: пишем это в интерфейсе, а не подразумеваем. */
function NoteSheet({ item, initial }: { item: AgendaItem; initial: string }) {
  const sheet = useSheet();
  const toast = useToast();
  const save = useSaveNote();
  const [body, setBody] = useState(initial);

  return (
    <>
      <h3>Заметка к записи</h3>
      <p>{item.child_name} · {item.service_title}</p>
      <div className="field">
        <label htmlFor="noteBody">Только для вас</label>
        <textarea id="noteBody" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Что было на встрече, что задано, на что смотреть дальше" />
        <span className="hint">Заметку не видят ни коллеги, ни администратор, ни родитель.</span>
      </div>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              { id: item.id, body },
              {
                onSuccess: () => {
                  toast('Заметка сохранена');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось сохранить'),
              },
            )
          }
        >
          Сохранить
        </button>
      </div>
    </>
  );
}

/** Неявка ≠ отмена: слот остаётся занятым, а судьбу оплаты выбирают явно. */
function NoShowSheet({ item }: { item: AgendaItem }) {
  const sheet = useSheet();
  const toast = useToast();
  const action = useAppointmentAction();
  const [payment, setPayment] = useState<'keep' | 'refund' | 'carry'>('keep');

  return (
    <>
      <h3>Клиент не пришёл</h3>
      <p>
        {item.child_name} · {item.service_title}. Время потрачено, поэтому слот останется занятым,
        а в отчёте по выплатам неявка пойдёт отдельной строкой.
      </p>
      <div className="field">
        <label htmlFor="nsPay">Оплата</label>
        <select id="nsPay" value={payment} onChange={(e) => setPayment(e.target.value as typeof payment)}>
          <option value="keep">Оставить организации</option>
          <option value="refund">Вернуть родителю</option>
          <option value="carry">Перенести на следующую запись</option>
        </select>
      </div>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid-danger"
          disabled={action.isPending}
          onClick={() =>
            action.mutate(
              { id: item.id, action: 'no-show', payment },
              {
                onSuccess: () => {
                  toast('Отмечена неявка');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
              },
            )
          }
        >
          Отметить неявку
        </button>
      </div>
    </>
  );
}

function CancelSheet({ item }: { item: AgendaItem }) {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const action = useAppointmentAction();
  const [reason, setReason] = useState('Я не смогу принять');
  const [refund, setRefund] = useState(true);

  return (
    <>
      <h3>Отменить запись</h3>
      <p>
        {item.child_name} · {item.service_title}, в {formatTime(item.starts_at_utc, me.center.tz, me.center.timeFormat)}.
        Слот освободится и снова станет доступен для записи.
      </p>
      <div className="field">
        <label htmlFor="caWhy">Причина</label>
        <select id="caWhy" value={reason} onChange={(e) => setReason(e.target.value)}>
          <option>Я не смогу принять</option>
          <option>Просьба родителя</option>
          <option>Ребёнок заболел</option>
          <option>Другое</option>
        </select>
      </div>
      <label className="check">
        <input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} />
        <span>Вернуть оплату родителю</span>
      </label>
      <Notice>Отмена по вине организации возвращается всегда, даже если до записи осталось меньше времени, чем в правиле.</Notice>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Не отменять</button>
        <button
          className="btn solid-danger"
          disabled={action.isPending}
          onClick={() =>
            action.mutate(
              { id: item.id, action: 'cancel', reason, refund },
              {
                onSuccess: () => {
                  toast('Запись отменена');
                  sheet.close();
                },
                onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось'),
              },
            )
          }
        >
          Отменить запись
        </button>
      </div>
    </>
  );
}

/** Перенос: показываем только свои свободные окна — сетку считает сервер. */
function MoveSheet({ item }: { item: AgendaItem }) {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const action = useAppointmentAction();
  const tz = me.center.tz;
  const tf = me.center.timeFormat;
  const slots = useStaffSlots({ specialistId: item.specialist_id, serviceId: item.service_id, childId: item.child_id });
  const days = slots.data ?? [];
  const [date, setDate] = useState<string | null>(null);
  const [startsAt, setStartsAt] = useState<number | null>(null);
  const selected = days.find((d) => d.date === date) ?? null;

  return (
    <>
      <h3>Перенести запись</h3>
      <p>
        {item.child_name} · {item.service_title}, сейчас в {formatTime(item.starts_at_utc, tz, tf)}.
        Показаны только ваши свободные окна.
      </p>

      {slots.isPending ? <p className="lead">Смотрим окна…</p> : null}
      {!slots.isPending && days.length === 0 ? <p className="lead">Свободных окон впереди нет.</p> : null}

      <div className="days">
        {days.map((d) => (
          <button
            key={d.date}
            type="button"
            className="day"
            aria-pressed={date === d.date}
            onClick={() => {
              setDate(d.date);
              setStartsAt(null);
            }}
          >
            <small>{new Intl.DateTimeFormat('ru-RU', { timeZone: 'UTC', weekday: 'short' }).format(Date.UTC(+d.date.slice(0, 4), +d.date.slice(5, 7) - 1, +d.date.slice(8), 12))}</small>
            <b>{Number(d.date.slice(8))}</b>
            <span className="free">{d.slots.length}</span>
          </button>
        ))}
      </div>

      <div className="slots">
        {selected
          ? selected.slots.map((s) => (
              <button key={s.startsAt} type="button" className="slot" aria-pressed={startsAt === s.startsAt} onClick={() => setStartsAt(s.startsAt)}>
                {s.time}
              </button>
            ))
          : <p className="lead" style={{ gridColumn: '1/-1' }}>Выберите день.</p>}
      </div>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!startsAt || action.isPending}
          onClick={() =>
            action.mutate(
              { id: item.id, action: 'reschedule', startsAt: startsAt! },
              {
                onSuccess: () => {
                  toast('Запись перенесена');
                  sheet.close();
                },
                onError: (e) => {
                  // Слот мог уйти, пока шторка была открыта: сервер решает, а не мы.
                  if (e instanceof ApiError && e.is('CHILD_BUSY')) {
                    const other = e.details as { display_name?: string } | undefined;
                    toast(other?.display_name
                      ? `В это время ребёнок занят у коллеги — ${other.display_name}`
                      : 'В это время ребёнок занят у другого специалиста');
                    setStartsAt(null);
                    return;
                  }
                  toast(e instanceof ApiError ? e.message : 'Не получилось перенести');
                },
              },
            )
          }
        >
          Перенести
        </button>
      </div>
    </>
  );
}

/** Что родитель написал при записи — лежит в `intake_json`. */
function intakeNote(item: AgendaItem): string | null {
  try {
    const raw = JSON.parse(item.intake_json ?? '{}') as Record<string, unknown>;
    const note = raw.note ?? raw.request;
    return typeof note === 'string' && note.trim() ? note : null;
  } catch {
    return null;
  }
}
